import { getFormatedClientTime } from 'CorePackage/MainModule/common/formatDate';
import { getServiceBaseURL } from 'APIPackage/MainModule/common/getServiceBaseURL';
import { APIServiceEnum } from 'APIPackage/MainModule/enums/APIServiceEnum';
import { apiDelete, apiGet, apiPost, apiPut } from 'APIPackage/MainModule/common/apiTools';
import { IErknmInfoExcludeDatesParams } from 'KnmErknmPackage/ErknmInfoModule/interfaces/IErknmInfoExcludeDatesParams';
import { IErknmInfoExcludeDatesResponse } from 'KnmErknmPackage/ErknmInfoModule/interfaces/IErknmInfoExcludeDatesResponse';
import { CreatePreventionModalDTO } from 'KnmPackage/MainModule/common/convertCreateModalPreventionToDTO';

import { IKnm } from '../interfaces/IKnm';
import { IKnmParams } from '../interfaces/IKnmParams';
import { IKnmInfoPlannedActivities } from '../interfaces/IKnmInfoPlannedActivities';
import { IKnmAccessPrivilege } from '../interfaces/IKnmAccessPrivilege';
import { IErvkCorrectionRequiredData } from '../interfaces/IErvkCorrectionRequiredData';
import { IErknmCopyResponse } from '../interfaces/IErknmCopyResponse';
import { IKnmControlObject } from '../interfaces/IKnmControlObject';
import { getLawInterfaceFromStorage } from '../../../CorePackage/MainModule/common/getLawInterfaceFromStorage';
import { LawInterfacesEnum } from '../../../CorePackage/MainModule/enums/LawInterfacesEnum';
import { IKnmHistoryItem } from '../interfaces/IKnmHistoryItem';
import { IErknmPrescribeCancel } from '../interfaces/IErknmPrescribeCancel';
import { IPmCancelWarning } from '../interfaces/IPmCancelWarning';
import { IKnmSubject248 } from '../interfaces/IKnmSubject248';
import { KnmDraftTypesEnum } from '../enums/KnmDraftTypesEnum';
import { IErknmAgreement } from '../interfaces/IErknmAgreement';
import { IKnmRequirementsNpa248 } from '../interfaces/IKnmRequirementsNpa248';
import { IDistrict } from '../interfaces/IDistrict';

interface IFetchKnmCreate {
  data: IKnmParams;
}

export async function fetchKnmCreate({ data }: IFetchKnmCreate): Promise<IKnm> {
  return apiPost<IKnm>({
    url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm`,
    body: { ...data, clientTime: getFormatedClientTime() },
  });
}

export async function fetchKnmUpdateById(data: IKnmParams): Promise<IKnm> {
  return apiPut<IKnm>(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm`, {
    ...data,
    clientTime: getFormatedClientTime(),
  });
}

export async function fetchKnmAdminUpdateById(data: IKnmParams, changeReason: string): Promise<IKnm> {
  const queryParams = new URLSearchParams();

  queryParams.append('changeReason', changeReason);

  return apiPut<IKnm>(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/update-subjects-objects?${queryParams}`, {
    ...data,
    clientTime: getFormatedClientTime(),
  });
}

export async function fetchKnmDraft(id: number): Promise<IKnm> {
  return apiGet<IKnm>(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/drafts/${KnmDraftTypesEnum.KNM_PM}/${id}`);
}

export async function fetchKnmSaveDraft(data: IKnmParams): Promise<IKnm> {
  return apiPut<IKnm>(
    `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/drafts/${KnmDraftTypesEnum.KNM_PM}/${data.id}`,
    {
      ...data,
      clientTime: getFormatedClientTime(),
    },
  );
}

export async function fetchKnmDraftDelete(id: number): Promise<void> {
  return apiDelete(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/drafts/${KnmDraftTypesEnum.KNM_PM}/${id}`);
}

export async function fetchKnmDeleteById(id: number): Promise<void> {
  return apiDelete(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/${id}`);
}

export async function fetchLateChangesFields(id: number): Promise<IKnmHistoryItem[]> {
  return apiGet<IKnmHistoryItem[]>(`${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm/late-changes-fields/${id}`);
}

export async function fetchKnm(id: number): Promise<IKnm> {
  const originalKnmData = await apiGet<IKnm>(`${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm/${id}`);

  const isErknm: boolean = getLawInterfaceFromStorage() === LawInterfacesEnum.FZ_248;

  if (isErknm) {
    try {
      const knmControlObjects = await apiGet<IKnmControlObject[]>(
        `${getServiceBaseURL(APIServiceEnum.BASE_SERVICE)}/knm/${id}/control-objects`,
      );

      originalKnmData.knmErknm?.objects?.forEach(object => {
        const controlObject = object;

        const tmpErvk: IKnmControlObject | undefined = knmControlObjects.filter(
          obj => obj.registryEntryId === controlObject.ervkNumber,
        )[0];

        controlObject.tmpErvkCosId = tmpErvk?.cosId || controlObject.ervkNumber;
      });

      originalKnmData.pmErknm?.objects?.forEach(object => {
        const controlObject = object;

        const tmpErvk: IKnmControlObject | undefined = knmControlObjects.filter(
          obj => obj.registryEntryId === controlObject.ervkNumber,
        )[0];

        controlObject.tmpErvkCosId = tmpErvk?.cosId || controlObject.ervkNumber;
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);

      return originalKnmData;
    }
  }

  return originalKnmData;
}

/**
 * Получает значения дат, заблокированных в календаре выбора
 * даты начала и окончания КНМ
 *
 * @param data
 * @returns Promise<IErknmInfoExcludeDatesResponse>
 */
export async function fetchExcludeDates(data: IErknmInfoExcludeDatesParams): Promise<IErknmInfoExcludeDatesResponse> {
  return apiPost({
    url: `${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/production-calendar/knm-excluded-dates`,
    body: data,
  });
}

/**
 * Получает количество рабочих дней между датами
 */
export async function fetchWorkingDaysCount(startDate: string, endDate: string): Promise<{ count: number }> {
  return apiPost({
    url: `${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/production-calendar/get-working-days-count`,
    body: { startDate, endDate },
  });
}

/**
 * Получает Ограничения для даты согласования соглашения
 */
export async function fetchAgreementApproveDate(dateOnApprove: string): Promise<string> {
  const queryParams = new URLSearchParams();

  queryParams.append('agreementDate', dateOnApprove);

  return apiGet(
    `${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/production-calendar/agreement-approve-date?${queryParams}`,
  );
}

export async function fetchPlannedActivities(
  knoId: string,
  supervisionId: string,
  id: number | null,
): Promise<IKnmInfoPlannedActivities> {
  const queryParams = new URLSearchParams();

  queryParams.append('knoId', knoId);
  queryParams.append('supervisionId', supervisionId);
  typeof id === 'number' && queryParams.append('knmId', id.toString());

  return apiGet(`${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/erknm/planned-activities?${queryParams}`);
}

export async function fetchOrders(
  knoId: string,
  supervisionId: string,
  conditionId: string,
  isPm: boolean,
  typeId: string,
  isDecision?: boolean,
): Promise<IKnmInfoPlannedActivities> {
  const queryParams = new URLSearchParams();

  queryParams.append('knoVersionId', knoId);
  queryParams.append('supervisionVersionId', supervisionId);
  queryParams.append('conditionId', conditionId);
  if (isDecision) {
    queryParams.append('decisionKindId', typeId);
  } else {
    queryParams.append(isPm ? 'pmKindId' : 'typeErknmCode', typeId);
  }

  let pathKey = 'decision';

  if (!isDecision) {
    pathKey = isPm ? 'pm' : 'knm';
  }

  return apiGet(`${getServiceBaseURL(APIServiceEnum.LK_SERVICE)}/catalogs/orders/${pathKey}?${queryParams}`);
}

export async function fetchUpdateAccessPrivilege(id: number, data: IKnmAccessPrivilege[]): Promise<IKnm> {
  return apiPut<IKnm>(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/access-privilege/${id}`, data);
}

export async function fetchKnmCheckChanges(id: number): Promise<boolean> {
  return apiPost<boolean>({
    url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/${id}/check-changes`,
  });
}

export async function fetchIsKnmCanBeSigned(id: number): Promise<boolean> {
  return apiGet<boolean>(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/${id}/is-can-be-signed`);
}

export async function fetchExcludedViolationsFromReinspection(id: number): Promise<IKnmRequirementsNpa248[]> {
  return apiGet<IKnmRequirementsNpa248[]>(
    `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/${id}/excluded-violations-from-reinspection`,
  );
}

export async function fetchCheckKnoSupervisionLink(id: number): Promise<IErvkCorrectionRequiredData> {
  return apiGet<IErvkCorrectionRequiredData>(
    `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/${id}/check-kno-supervision-link`,
  );
}

export async function fetchInvalidFields(id: number): Promise<string[]> {
  return apiGet(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/invalid-fields/${id}`);
}

export async function fetchErknmCopy(data: IErknmCopyResponse): Promise<number> {
  return apiPost({ url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/copy`, body: data });
}

export async function fetchErknmActCancel(data: IErknmPrescribeCancel): Promise<number> {
  return apiPut(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/results/cancel`, data);
}
export async function fetchErknmPrescribeCancel(data: IErknmPrescribeCancel): Promise<number> {
  return apiPut(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/prescription/cancel`, data);
}

export async function fetchPmCancelWarning(data: IPmCancelWarning): Promise<number> {
  return apiPut(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/warning/cancel`, data);
}

export async function fetchKnmRestoreLatestSignedVersion(id: number | null): Promise<IKnm> {
  return apiPost({
    url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/${id}/restore-to-latest-signed`,
    body: {},
  });
}

export async function fetchKnmSubjectsWithImpossibilityActNotRepeatedInLinkedKnms(
  id: number | null,
  currentPassportId?: number | null,
): Promise<{ subjects: IKnmSubject248[]; linkedPassportIds: { id: number; erpId: string }[] }> {
  return apiGet(
    `${getServiceBaseURL(
      APIServiceEnum.KNM_SERVICE,
    )}/knm/subjects-with-impossibility-act-not-repeated-in-linked-knms?knmId=${id}${
      currentPassportId ? `&currentKnmId=${currentPassportId}` : ''
    }`,
  );
}

export async function fetchKnmAgreementTemplateDownload(
  id: number,
  agreement: IErknmAgreement,
  isDecisionPassport: boolean,
): Promise<Blob> {
  const queryParams = new URLSearchParams();

  queryParams.append('isDecisionPassport', isDecisionPassport.toString());

  return apiPost<Blob>({
    url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/${id}/agreement-template-download?${queryParams}`,
    body: agreement,
  });
}

export async function fetchCreatePmFromModalForm(dto: CreatePreventionModalDTO) {
  return apiPost({
    url: `${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/create-pm-modal`,
    body: dto,
  });
}

export async function fetchGetKnmUnresolvedViolations(knmId: string): Promise<boolean> {
  return apiGet(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/knm/${knmId}/has-knm-unresolved-violations`);
}

/**
 * Определение территориальной единицы (субъекта РФ) по адресу ФИАС и/или коду региона / UUID ФИАС / id справочника.
 */
export async function fetchResolveRegion(
  address?: string | null,
  regionCode?: string | null,
): Promise<IDistrict | null> {
  if (!address && !regionCode) {
    return null;
  }

  const queryParams = new URLSearchParams();

  if (address) {
    queryParams.append('address', address);
  }
  if (regionCode) {
    queryParams.append('regionCode', regionCode);
  }

  return apiGet<IDistrict | null>(`${getServiceBaseURL(APIServiceEnum.KNM_SERVICE)}/region/resolve?${queryParams}`);
}
